'use client'

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Group, Panel, type PanelImperativeHandle, Separator } from 'react-resizable-panels'

import { cn } from '../../utils/cn'

type ResizablePanelGroupProps = React.ComponentProps<typeof Group>

function ResizablePanelGroup({ className, ...props }: ResizablePanelGroupProps) {
  return (
    <Group
      data-slot="resizable-panel-group"
      className={cn('h-full w-full', className)}
      {...props}
    />
  )
}

type ResizablePanelProps = React.ComponentProps<typeof Panel>

function ResizablePanel(props: ResizablePanelProps) {
  return <Panel data-slot="resizable-panel" {...props} />
}

const EASING = 'ease-[cubic-bezier(0.33,1,0.68,1)]'
const TRANSITION = `transition-all duration-200 ${EASING}`
const LINE_SHARED = `bg-primary pointer-events-none absolute ${TRANSITION}`

const orientationConfig = {
  vertical: {
    handle: 'h-2.5 cursor-row-resize',
    lineFirst: `${LINE_SHARED} left-0 right-1/2 top-1/2 -translate-y-1/2 rounded-l-full origin-right`,
    lineSecond: `${LINE_SHARED} left-1/2 right-0 top-1/2 -translate-y-1/2 rounded-r-full origin-left`,
    scaleShow: 'scale-x-100',
    scaleHide: 'scale-x-0',
    thicknessNormal: 'h-0.5',
    thicknessActive: 'h-1'
  },
  horizontal: {
    handle: 'w-2.5 cursor-col-resize',
    lineFirst: `${LINE_SHARED} top-0 bottom-1/2 left-1/2 -translate-x-1/2 rounded-t-full origin-bottom`,
    lineSecond: `${LINE_SHARED} top-1/2 bottom-0 left-1/2 -translate-x-1/2 rounded-b-full origin-top`,
    scaleShow: 'scale-y-100',
    scaleHide: 'scale-y-0',
    thicknessNormal: 'w-0.5',
    thicknessActive: 'w-1'
  }
} as const

type ResizableHandleState = {
  isHovered: boolean
  isDragging: boolean
}

const ResizableHandleContext = createContext<ResizableHandleState | null>(null)

function useResizableHandle(): ResizableHandleState {
  const context = useContext(ResizableHandleContext)

  if (!context) {
    throw new Error('useResizableHandle must be used within a ResizableHandle')
  }

  return context
}

type ResizableHandleProps = Omit<React.ComponentProps<typeof Separator>, 'children'> & {
  orientation?: 'horizontal' | 'vertical'
  onPointerClick?: () => void
  children?: React.ReactNode
}

function ResizableHandle({
  orientation = 'vertical',
  onPointerClick,
  className,
  children,
  ...props
}: ResizableHandleProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const didDragRef = useRef(false)
  const onPointerClickRef = useRef(onPointerClick)
  onPointerClickRef.current = onPointerClick
  const cleanupListenersRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    return () => cleanupListenersRef.current?.()
  }, [])

  const handlePointerDown = useCallback(() => {
    didDragRef.current = false

    function handlePointerMove() {
      if (didDragRef.current) return

      didDragRef.current = true
      setIsDragging(true)
    }

    function cleanup() {
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerUp)
      cleanupListenersRef.current = null
    }

    function handlePointerUp() {
      cleanup()

      if (!didDragRef.current) {
        onPointerClickRef.current?.()
      }
      didDragRef.current = false
      setIsDragging(false)
    }

    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerup', handlePointerUp)
    cleanupListenersRef.current = cleanup
  }, [])

  const { handle, lineFirst, lineSecond, scaleShow, scaleHide, thicknessNormal, thicknessActive } =
    orientationConfig[orientation]

  const showLine = isHovered || isDragging

  return (
    <ResizableHandleContext.Provider value={{ isHovered, isDragging }}>
      <Separator
        {...props}
        data-slot="resizable-handle"
        className={cn('relative shrink-0 outline-none', handle, className)}
        onPointerDown={handlePointerDown}
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsHovered(false)}
      >
        <div
          className={cn(
            lineFirst,
            showLine ? `${scaleShow} opacity-100` : `${scaleHide} opacity-0`,
            isDragging ? thicknessActive : thicknessNormal
          )}
        />
        <div
          className={cn(
            lineSecond,
            showLine ? `${scaleShow} opacity-100` : `${scaleHide} opacity-0`,
            isDragging ? thicknessActive : thicknessNormal
          )}
        />
        {children}
      </Separator>
    </ResizableHandleContext.Provider>
  )
}

export { ResizableHandle, ResizablePanel, ResizablePanelGroup, useResizableHandle }
export type { PanelImperativeHandle }
